import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { API } from '../config/api';
import { motion } from 'framer-motion';
import {
  Music, Video, Star, TrendingUp, Mic, Radio,
  Headphones, Building2, Users, FileText, Sparkles,
  ArrowRight, Play, ChevronLeft, ChevronRight
} from 'lucide-react';

const SLOT_CONFIG = {
  top_song_week: { icon: Music, color: 'from-orange-600/40 to-amber-500/20', border: 'border-orange-500/40', badge: 'bg-orange-500/90', text: 'text-orange-400', label: 'Top Song' },
  top_video_week: { icon: Video, color: 'from-red-600/40 to-rose-500/20', border: 'border-red-500/40', badge: 'bg-red-500/90', text: 'text-red-400', label: 'Top Video' },
  featured_artist_week: { icon: Star, color: 'from-purple-600/40 to-indigo-500/20', border: 'border-purple-500/40', badge: 'bg-purple-500/90', text: 'text-purple-400', label: 'Featured Artist' },
  artist_on_rise_week: { icon: TrendingUp, color: 'from-emerald-600/40 to-green-500/20', border: 'border-emerald-500/40', badge: 'bg-emerald-500/90', text: 'text-emerald-400', label: 'Rising Star' },
  top_podcaster_week: { icon: Mic, color: 'from-blue-600/40 to-cyan-500/20', border: 'border-blue-500/40', badge: 'bg-blue-500/90', text: 'text-blue-400', label: 'Top Podcaster' },
  top_streamer_week: { icon: Radio, color: 'from-pink-600/40 to-fuchsia-500/20', border: 'border-pink-500/40', badge: 'bg-pink-500/90', text: 'text-pink-400', label: 'Top Streamer' },
  top_producer_week: { icon: Headphones, color: 'from-violet-600/40 to-purple-500/20', border: 'border-violet-500/40', badge: 'bg-violet-500/90', text: 'text-violet-400', label: 'Top Producer' },
  top_studio_week: { icon: Building2, color: 'from-amber-600/40 to-yellow-500/20', border: 'border-amber-500/40', badge: 'bg-amber-500/90', text: 'text-amber-400', label: 'Top Studio' },
  community_spotlight_week: { icon: Users, color: 'from-teal-600/40 to-emerald-500/20', border: 'border-teal-500/40', badge: 'bg-teal-500/90', text: 'text-teal-400', label: 'Community' },
  top_story_week: { icon: FileText, color: 'from-sky-600/40 to-blue-500/20', border: 'border-sky-500/40', badge: 'bg-sky-500/90', text: 'text-sky-400', label: 'Top Story' },
  spotlight_interview_week: { icon: Mic, color: 'from-indigo-600/40 to-violet-500/20', border: 'border-indigo-500/40', badge: 'bg-indigo-500/90', text: 'text-indigo-400', label: 'Spotlight' },
  soundstage_spotlight_paid: { icon: Sparkles, color: 'from-yellow-600/40 to-orange-500/20', border: 'border-yellow-500/40', badge: 'bg-yellow-500/90', text: 'text-yellow-400', label: 'Featured' },
};

function SlotCard({ slot, config, index }) {
  const Icon = config.icon;
  const meta = slot.selected_meta || {};
  const imageUrl = meta.cover_url || meta.profile_image_url;
  const linkUrl = meta.link_url || '/charts';

  const handleClick = () => {
    // Track click
    try {
      fetch(`${API}/stats/track-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot_key: slot.slot_key }),
      }).catch(() => {});
    } catch {}
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      viewport={{ once: true }}
      className="flex-shrink-0 w-[260px] sm:w-[280px]"
    >
      <Link to={linkUrl} onClick={handleClick}>
        <Card
          className={`glass-card ${config.border} h-full overflow-hidden group cursor-pointer hover:scale-[1.02] transition-transform duration-200`}
          data-testid={`trending-slot-${slot.slot_key}`}
        >
          <div className={`relative aspect-[4/3] bg-gradient-to-br ${config.color}`}>
            {imageUrl ? (
              <img
                src={imageUrl.startsWith('http') ? imageUrl : `${API.replace('/api', '')}${imageUrl}`}
                alt={meta.title || config.label}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon className={`w-12 h-12 ${config.text} opacity-50`} />
              </div>
            )}
            <div className="absolute top-2 left-2">
              <Badge className={`${config.badge} text-foreground text-xs`}>
                <Icon className="w-3 h-3 mr-1" />
                {config.label}
              </Badge>
            </div>
            {slot.selection_mode === 'paid' && (
              <div className="absolute top-2 right-2">
                <Badge className="bg-yellow-500/90 text-black text-xs">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Promoted
                </Badge>
              </div>
            )}
            {(slot.slot_type === 'track' || slot.slot_type === 'video') && (
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-12 h-12 rounded-full bg-muted/60 backdrop-blur-sm flex items-center justify-center">
                  <Play className="w-5 h-5 text-white ml-0.5" />
                </div>
              </div>
            )}
          </div>
          <div className="p-3">
            <p className="font-heading text-sm font-bold uppercase truncate">
              {meta.title || slot.label}
            </p>
            {meta.handle && (
              <p className={`text-xs ${config.text} mt-1`}>{meta.handle ? `@${meta.handle}` : ""}</p>
            )}
          </div>
        </Card>
      </Link>
    </motion.div>
  );
}

export default function TrendingSlots() {
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(true);
  const [scrollPos, setScrollPos] = useState(0);

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    try {
      const res = await fetch(`${API}/featured-slots`);
      if (res.ok) {
        const data = await res.json();
        setSlots(data.slots || {});
      }
    } catch (err) {
      console.error('Failed to fetch featured slots:', err);
    } finally {
      setLoading(false);
    }
  };

  const filledSlots = Object.values(slots).filter(s => s.selected_id);

  // Track impressions for visible slots
  useEffect(() => {
    if (filledSlots.length > 0) {
      filledSlots.forEach(slot => {
        try {
          fetch(`${API}/stats/track-impression`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ slot_key: slot.slot_key }),
          }).catch(() => {});
        } catch {}
      });
    }
  }, [slots]);

  if (loading || filledSlots.length === 0) return null;

  const scrollContainer = (dir) => {
    const el = document.getElementById('trending-scroll');
    if (el) {
      const amount = dir === 'left' ? -300 : 300;
      el.scrollBy({ left: amount, behavior: 'smooth' });
      setScrollPos(el.scrollLeft + amount);
    }
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8" data-testid="trending-slots-section">
      <div className="max-w-[1800px] mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Badge className="mb-3 bg-secondary/20 text-secondary">
              <TrendingUp className="w-3 h-3 mr-1" />
              Auto-Trending
            </Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold uppercase">
              Trending on <span className="text-secondary">Soundstage</span>
            </h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Algorithmically selected based on plays, likes, and engagement this week
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => scrollContainer('left')}
              className="border-border hover:bg-muted/50"
              data-testid="trending-scroll-left"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => scrollContainer('right')}
              className="border-border hover:bg-muted/50"
              data-testid="trending-scroll-right"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div
          id="trending-scroll"
          className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {filledSlots.map((slot, i) => {
            const config = SLOT_CONFIG[slot.slot_key] || SLOT_CONFIG.soundstage_spotlight_paid;
            return <SlotCard key={slot.slot_key} slot={slot} config={config} index={i} />;
          })}
        </div>

        <div className="text-center mt-8">
          <Link to="/charts">
            <Button variant="outline" className="border-secondary/50 hover:bg-secondary/10">
              View Full Charts & Rankings
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
